import React, { useState, useEffect, useCallback } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import dayjs from "dayjs";
import ManageExtraIncomeModal from "../../../Components/Modals/ManageExtraIncomeModal/ManageExtraIncomeModal";
import BusinessExpensesModal from "./BusinessExpensesModal";

interface RevenueSummaryProps {
  clientId: string;
  selectedMonths: string[];
  locationFilters: string[];
  businessExpenses?: { rsd: number; eur: number };
  onLoadingChange?: (loading: boolean) => void;
  defaultValues?: any[];
  customValues?: any[];
  dailyExpenses?: any[];
  locations?: any[];
  categories?: any[];
  generationStatus?: Record<string, any>;
  /** Total goods value on stock (from InventorySection) */
  goodsStock?: number;
}

interface RevenueData {
  totalRevenue: number;
  totalRevenueWithExtra: number;
  extraIncome: number;
  gotovinaTotal: number;
  internaGotovinaTotal: number;
  cardTotal: number;
  deliveryTotal: number;
  bankTransferTotal: number;
  drugoTotal: number;
  deductionsTotal: number;
  businessExpenses: { rsd: number; eur: number };
  netResult: { rsd: number; eur: number };
}

const fmt = (n: number) =>
  new Intl.NumberFormat("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);

export const RevenueSummary: React.FC<RevenueSummaryProps> = ({
  clientId,
  selectedMonths,
  locationFilters,
  businessExpenses = { rsd: 0, eur: 0 },
  onLoadingChange,
  defaultValues = [],
  customValues = [],
  dailyExpenses = [],
  locations = [],
  categories = [],
  generationStatus = {},
  goodsStock = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isExtraIncomeModalOpen, setIsExtraIncomeModalOpen] = useState(false);
  const [isBusinessExpensesModalOpen, setIsBusinessExpensesModalOpen] = useState(false);
  const [revenueData, setRevenueData] = useState<RevenueData>({
    totalRevenue: 0,
    totalRevenueWithExtra: 0,
    extraIncome: 0,
    gotovinaTotal: 0,
    internaGotovinaTotal: 0,
    cardTotal: 0,
    deliveryTotal: 0,
    bankTransferTotal: 0,
    drugoTotal: 0,
    deductionsTotal: 0,
    businessExpenses: { rsd: 0, eur: 0 },
    netResult: { rsd: 0, eur: 0 },
  });

  const monthsKey = selectedMonths.sort().join(",");
  const locationsKey = locationFilters.sort().join(",");

  useEffect(() => {
    setRevenueData((prev) => {
      const netResultRSD = prev.totalRevenueWithExtra - prev.deductionsTotal - businessExpenses.rsd;
      return { ...prev, businessExpenses, netResult: { rsd: netResultRSD, eur: 0 - businessExpenses.eur } };
    });
  }, [businessExpenses]);

  const fetchRevenueData = useCallback(async () => {
    if (!clientId || selectedMonths.length === 0) return;
    setLoading(true);
    onLoadingChange?.(true);
    try {
      const sortedMonths = [...selectedMonths].sort();
      const startDate = dayjs(sortedMonths[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs(sortedMonths[sortedMonths.length - 1], "YYYY-MM").endOf("month").format("YYYY-MM-DD");
      const params: any = { startDate, endDate };
      if (locationFilters.length > 0) params.locationIds = locationFilters.join(",");

      const { data } = await axios.get(`${BASE_API_URL}/admin/${clientId}/getRevenueByRange`, { params });
      let revenues = data.revenues || [];
      if (locationFilters.length > 0) {
        revenues = revenues.filter((r: any) => locationFilters.includes(r.locationId?._id));
      }

      let extraIncomeTotal = 0;
      try {
        const extraParams: any = { clientId, startDate, endDate };
        if (locationFilters.length > 0) extraParams.locationIds = locationFilters.join(",");
        const sumRes = await axios.get(`${BASE_API_URL}/extra-income/sum`, { params: extraParams });
        extraIncomeTotal = sumRes.data.total || 0;
      } catch { /* noop */ }

      let gotovinaTotal = 0, internaGotovinaTotal = 0, cardTotal = 0, deliveryTotal = 0, bankTransferTotal = 0, drugoTotal = 0, deductionsTotal = 0;
      revenues.forEach((r: any) => {
        gotovinaTotal += Number(r.cashAmount || 0);
        internaGotovinaTotal += Number(r.cash || 0);
        cardTotal += Number(r.cardAmount || 0);
        deliveryTotal += Number(r.delivery || 0);
        bankTransferTotal += Number(r.bankTransfer || 0);
        drugoTotal += Number(r.drugo || 0);
        if (Array.isArray(r.paymentFromTheRevenue)) {
          r.paymentFromTheRevenue
            .filter((item: any) => !item.other)
            .forEach((item: any) => { deductionsTotal += Number(item.total || 0); });
        }
      });

      const totalRevenue = gotovinaTotal + internaGotovinaTotal + cardTotal + deliveryTotal + bankTransferTotal + drugoTotal;
      const totalRevenueWithExtra = totalRevenue + extraIncomeTotal;
      const netResultRSD = totalRevenueWithExtra - deductionsTotal - businessExpenses.rsd;

      setRevenueData({
        totalRevenue, totalRevenueWithExtra, extraIncome: extraIncomeTotal,
        gotovinaTotal, internaGotovinaTotal, cardTotal, deliveryTotal, bankTransferTotal, drugoTotal, deductionsTotal,
        businessExpenses,
        netResult: { rsd: netResultRSD, eur: 0 - businessExpenses.eur },
      });
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, monthsKey, locationsKey, businessExpenses.rsd, businessExpenses.eur]);

  useEffect(() => {
    if (clientId && selectedMonths.length > 0) fetchRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, monthsKey, locationsKey, businessExpenses.rsd, businessExpenses.eur]);

  const getDateRange = (): [dayjs.Dayjs | null, dayjs.Dayjs | null] => {
    if (selectedMonths.length === 0) return [null, null];
    const sorted = [...selectedMonths].sort();
    return [
      dayjs(sorted[0], "YYYY-MM").startOf("month"),
      dayjs(sorted[sorted.length - 1], "YYYY-MM").endOf("month"),
    ];
  };

  const getPeriodLabel = () => {
    if (selectedMonths.length === 0) return "";
    const sorted = [...selectedMonths].sort();
    if (sorted.length === 1) {
      const d = dayjs(sorted[0], "YYYY-MM");
      return d.format("MMM YYYY");
    }
    return `${dayjs(sorted[0], "YYYY-MM").format("MMM YYYY")} – ${dayjs(sorted[sorted.length - 1], "YYYY-MM").format("MMM YYYY")}`;
  };

  const net = revenueData.netResult.rsd;
  const isPositive = net >= 0;
  const stvarniProfit = net - goodsStock;
  const isStvarniPositive = stvarniProfit >= 0;

  const STRUCT = [
    { label: "Gotovina",            value: revenueData.gotovinaTotal,        color: "#16a34a" },
    { label: t("Cards"),            value: revenueData.cardTotal,            color: "#0ea5e9" },
    { label: t("Bank Transfer"),    value: revenueData.bankTransferTotal,    color: "#6366f1" },
    { label: t("Internal cash"),    value: revenueData.internaGotovinaTotal, color: "#15803d" },
    { label: t("Delivery"),         value: revenueData.deliveryTotal,        color: "#ea580c" },
    ...(revenueData.drugoTotal > 0 ? [{ label: t("Other"), value: revenueData.drugoTotal, color: "#9ca3af" }] : []),
  ];

  return (
    <section className="fin-overview">
      <div className="fo-head">
        <div>
          <h2>{t("Financial Overview")}</h2>
          <div className="fo-sub">
            {t("How we arrived at the net amount")}
          </div>
        </div>
        <span className="fo-period-tag">{getPeriodLabel()}</span>
      </div>

      <Spin spinning={loading}>
        <div className="fo-flow">
          {/* ── Income block ── */}
          <div className="fo-block income">
            <div className="fo-block-head">
              <span className="fo-icon income">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5"/><path d="M5 12l7-7 7 7"/>
                </svg>
              </span>
              <div>
                <div className="fo-label">{t("Revenue · Total")}</div>
                <div className="fo-amount">{fmt(revenueData.totalRevenue)} <span className="cur">RSD</span></div>
                <div className="fo-meta">{t("Total revenue for selected period")}</div>
              </div>
            </div>

            {revenueData.extraIncome > 0 && (
              <div className="fo-extra-income" onClick={() => setIsExtraIncomeModalOpen(true)} role="button" tabIndex={0}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
                <span>+ {fmt(revenueData.extraIncome)} RSD {t("(extra income)")}</span>
              </div>
            )}

            <div className="fo-struct">
              <div className="fo-struct-title">{t("Revenue Structure")}</div>
              <div className="fo-struct-grid">
                {STRUCT.map((s) => (
                  <div className="fo-struct-item" key={s.label}>
                    <span className="dot" style={{ background: s.color }} />
                    {s.label}
                    <b>{fmt(s.value)} RSD</b>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Connector ── */}
          <div className="fo-connector">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M5 12h14M13 6l6 6-6 6"/>
            </svg>
          </div>

          {/* ── Right stack ── */}
          <div className="fo-stack">
            {/* Deductions */}
            <div className="fo-mini">
              <span className="fo-icon mini neutral">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9"/><path d="M8 12h8"/>
                </svg>
              </span>
              <div className="fo-mini-body">
                <div className="fo-mini-label">{t("Deductions from daily revenue")}</div>
                <div className="fo-mini-amount">{fmt(revenueData.deductionsTotal)} <span className="cur">RSD</span></div>
              </div>
            </div>

            {/* Business expenses */}
            <div className="fo-mini neg">
              <span className="fo-icon mini neg">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                </svg>
              </span>
              <div className="fo-mini-body">
                <div className="fo-mini-label">{t("Regular business expenses")}</div>
                <div className="fo-mini-amount neg">
                  {fmt(revenueData.businessExpenses.rsd)} <span className="cur">RSD</span>
                  {revenueData.businessExpenses.eur > 0 && (
                    <span style={{ fontSize: 12, marginLeft: 8, color: "#dc2626", opacity: 0.8 }}>
                      + {fmt(revenueData.businessExpenses.eur)} EUR
                    </span>
                  )}
                </div>
              </div>
              <button className="fo-mini-link" onClick={() => setIsBusinessExpensesModalOpen(true)}>
                {t("Details")}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M9 6l6 6-6 6"/>
                </svg>
              </button>
            </div>

            {/* Net result */}
            <div className={`fo-net${isPositive ? " pos" : ""}`}>
              <div className="fo-net-head">
                <span className={`fo-icon${isPositive ? " net-ok" : " net"}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/>
                  </svg>
                </span>
                <div>
                  <div className={`fo-net-label${isPositive ? " pos" : " neg"}`}>{t("Net Result")}</div>
                  <div className="fo-net-meta">{t("Revenue − expenses")}</div>
                </div>
              </div>
              <div className={`fo-net-amount${isPositive ? " pos" : " neg"}`}>
                {!isPositive && "−"}{fmt(Math.abs(net))} <span className="cur">RSD</span>
                {revenueData.netResult.eur !== 0 && (
                  <span style={{ fontSize: 13, marginLeft: 10, opacity: 0.7 }}>
                    {revenueData.netResult.eur >= 0 ? "+" : "−"} {fmt(Math.abs(revenueData.netResult.eur))} EUR
                  </span>
                )}
              </div>
              <div className="fo-net-bar">
                <div style={{
                  width: revenueData.totalRevenueWithExtra > 0
                    ? `${Math.min(100, (Math.max(0, net) / revenueData.totalRevenueWithExtra) * 100)}%`
                    : "0%",
                  background: isPositive
                    ? "linear-gradient(90deg, #16a34a, #22c55e)"
                    : "linear-gradient(90deg, #dc2626, #ef4444)",
                }} />
              </div>
            </div>

            {/* Goods consumed (only shown when inventory has been entered) */}
            {goodsStock > 0 && (
              <>
                <div className="fo-mini neg" style={{ marginTop: 6 }}>
                  <span className="fo-icon mini neg">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                  </span>
                  <div className="fo-mini-body">
                    <div className="fo-mini-label">{t("Warehouse stock")}</div>
                    <div className="fo-mini-amount neg">
                      {fmt(goodsStock)} <span className="cur">RSD</span>
                    </div>
                  </div>
                </div>

                {/* Real profit = Net result − Warehouse stock */}
                <div className={`fo-net${isStvarniPositive ? " pos" : ""}`} style={{ marginTop: 6 }}>
                  <div className="fo-net-head">
                    <span className={`fo-icon${isStvarniPositive ? " net-ok" : " net"}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                      </svg>
                    </span>
                    <div>
                      <div className={`fo-net-label${isStvarniPositive ? " pos" : " neg"}`}>{t("Real Profit")}</div>
                      <div className="fo-net-meta">{t("Net result − warehouse stock")}</div>
                    </div>
                  </div>
                  <div className={`fo-net-amount${isStvarniPositive ? " pos" : " neg"}`}>
                    {!isStvarniPositive && "−"}{fmt(Math.abs(stvarniProfit))} <span className="cur">RSD</span>
                  </div>
                  <div className="fo-net-bar">
                    <div style={{
                      width: revenueData.totalRevenueWithExtra > 0
                        ? `${Math.min(100, (Math.max(0, stvarniProfit) / revenueData.totalRevenueWithExtra) * 100)}%`
                        : "0%",
                      background: isStvarniPositive
                        ? "linear-gradient(90deg, #6366f1, #818cf8)"
                        : "linear-gradient(90deg, #dc2626, #ef4444)",
                    }} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Spin>

      <ManageExtraIncomeModal
        visible={isExtraIncomeModalOpen}
        onClose={() => setIsExtraIncomeModalOpen(false)}
        dateRange={getDateRange()}
        locationFilters={locationFilters}
        onUpdate={fetchRevenueData}
      />

      <BusinessExpensesModal
        visible={isBusinessExpensesModalOpen}
        onClose={() => setIsBusinessExpensesModalOpen(false)}
        defaultValues={defaultValues}
        customValues={customValues}
        dailyExpenses={dailyExpenses}
        locationFilters={locationFilters}
        selectedMonths={selectedMonths}
        locations={locations}
        categories={categories}
        generationStatus={generationStatus}
      />
    </section>
  );
};
